import { Card, Empty, Radio, RadioGroup, Skeleton, Typography } from "@douyinfe/semi-ui";
import { VChart } from "@visactor/react-vchart";
import { useEffect, useState } from "react";
import { DashboardFilters, FunnelDetailDto, HomeDashboardApi, MonthlyEntryItem } from "../api/homeDashboard";
import { useDashboardModule } from "../hooks/useDashboardModule";
import { chartBoxStyle, chartTitleStyle } from "../styles";
import { DrawerMode } from "./DetailDrawer";

interface Props {
  filters: DashboardFilters;
  isAdmin: boolean;
  currentTeamId?: number;
  openDrawer: (title: string, params: Record<string, any>, mode?: DrawerMode) => void;
}

const STAGE_MODE_MAP: Record<string, DrawerMode> = {
  "入库": "customer",
  "分配跟进": "assign",
  "有效通话": "call",
  "上门": "visit",
  "签约": "customer",
  "回款": "customer",
};

export default function ConversionFunnelCard({ filters, openDrawer }: Props) {
  const [entryMode, setEntryMode] = useState<"created" | "assigned">("created");
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const monthly = useDashboardModule<MonthlyEntryItem[]>(
    () => HomeDashboardApi.getFunnelMonthlyEntries(entryMode, filters.teamId, filters.saleIds),
    [entryMode, filters.teamId, JSON.stringify(filters.saleIds)],
    true
  );

  const detail = useDashboardModule<FunnelDetailDto | null>(
    () => selectedMonth
      ? HomeDashboardApi.getFunnelMonthDetail(selectedMonth, entryMode, filters.teamId, filters.saleIds)
      : Promise.resolve(null),
    [selectedMonth, entryMode, filters.teamId, JSON.stringify(filters.saleIds)],
    !!selectedMonth
  );

  useEffect(() => {
    if (!selectedMonth && monthly.data && monthly.data.length > 0) {
      setSelectedMonth(monthly.data[monthly.data.length - 1].month);
    }
  }, [monthly.data]);

  const barValues = (monthly.data || []).map(m => ({
    month: m.month, count: m.count,
  }));

  return (
    <Card>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
        <Typography.Text>入库定义：</Typography.Text>
        <RadioGroup value={entryMode} onChange={(e) => setEntryMode(e.target.value as "created" | "assigned")}>
          <Radio value="created">线索创建时间</Radio>
          <Radio value="assigned">首次分配时间</Radio>
        </RadioGroup>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div style={{ ...chartBoxStyle, flex: "0 0 40%", minWidth: 320 }}>
          <Typography.Text style={chartTitleStyle}>过去 12 个月客户入库数（点击切换右侧漏斗）</Typography.Text>
          {monthly.loading && <Skeleton.Title />}
          {monthly.data && monthly.data.length > 0 ? (
            <VChart spec={{
              type: "bar", data: [{ values: barValues }],
              xField: "month", yField: "count",
              color: {
                specified: selectedMonth ? { [selectedMonth]: "var(--semi-color-primary)" } : {},
              },
              bar: {
                state: {
                  unselected: { fill: "var(--semi-color-primary-light-default)" },
                  selected: { fill: "var(--semi-color-primary)" },
                },
              },
              label: { visible: true, position: "top", text: "{count}" },
            } as any} style={{ height: 260 }}
              onClick={(e: any) => {
                const month = e?.datum?.["month"];
                if (month) setSelectedMonth(month as string);
              }} />
          ) : !monthly.loading ? <Empty title="暂无月度入库数据" /> : null}
        </div>

        <div style={{ ...chartBoxStyle, flex: "1 1 360px" }}>
          <Typography.Text style={chartTitleStyle}>
            {selectedMonth ? `${selectedMonth} 月客户转化漏斗` : "请选择月份"}
          </Typography.Text>
          {detail.loading && <Skeleton.Title />}
          {detail.data ? (
            <VChart spec={{
              type: "funnel", data: [{ values: [
                { name: "入库", value: detail.data.total },
                { name: "分配跟进", value: detail.data.assigned },
                { name: "有效通话", value: detail.data.effectiveCall },
                { name: "上门", value: detail.data.visited },
                { name: "签约", value: detail.data.signed },
                { name: "回款", value: detail.data.paid },
              ]}],
              categoryField: "name", valueField: "value",
              color: { range: ["#0093FF", "#23C343", "#F7BA1E", "#FF7D00", "#F53F3F", "#86909C"] },
              label: { visible: true, position: "right", text: "{name}: {value}" },
            } as any} style={{ height: 280 }}
              onClick={(e: any) => {
                const stage = e?.datum?.["name"];
                if (stage && selectedMonth) {
                  const mode = STAGE_MODE_MAP[stage] || "customer";
                  openDrawer(`转化漏斗 - ${selectedMonth} - ${stage}`, { month: selectedMonth, entryMode, stage }, mode);
                }
              }} />
          ) : !detail.loading ? <Empty title="请选择月份查看漏斗" /> : null}
        </div>
      </div>
    </Card>
  );
}
